// ABOUTME: Subscribes to the saves and deletes other users make on the rows of a Data Table page,
// ABOUTME: patches the rows without pending edits and turns the others into conflicts to resolve.

import type {
    CellHighlight,
    CellHighlights,
    DataTableRemoteHandle,
    NodeContent,
} from '@ds/data-table';
import { NodeUpdateType } from '@src/__generated__/types';
import { useAuth } from '@src/hooks/auth/useAuth';
import type { MutableRefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
    NodesUpdatedSubscription,
    useNodesUpdatedSubscription,
} from '../graphql/nodesUpdated.generated';

type NodeUpdate = NodesUpdatedSubscription['nodesUpdated'];
export type RemoteEdit = NodeUpdate['edits'][number];

/** One affected row, as listed by the notice above the table. */
export type RemoteNoticeEntry = {
    nodeId: string;
    code: string;
    /** Column ids, `${contentTypeGroup}-${contentType}`, the other user changed. */
    fieldCodes: string[];
    kind: CellHighlight;
};

/** Incoming edits held back because the row has an open editor on a changed column. */
export type RemoteConflict = {
    nodeId: string;
    code: string;
    edits: RemoteEdit[];
    /** Changed columns whose editor was open when the edits arrived. */
    openColumns: string[];
};

/** Updates arriving within this window are routed together, so a bulk save flushes once. */
export const REMOTE_UPDATES_FLUSH_MS = 500;

const KIND_SEVERITY: Record<CellHighlight, number> = { updated: 0, conflict: 1, deleted: 2 };

export const getEditColumnId = (edit: RemoteEdit) => `${edit.contentTypeGroup}-${edit.contentType}`;

const getEditKey = (edit: RemoteEdit) =>
    `${getEditColumnId(edit)}-${edit.contentTypeGroupIndex ?? 0}`;

const markColumns = (columns: string[], kind: CellHighlight) =>
    Object.fromEntries(columns.map(column => [column, kind])) as Record<string, CellHighlight>;

/** Merges edits of the same content, the later one winning. */
const mergeEdits = (earlier: RemoteEdit[], later: RemoteEdit[]) => {
    const byKey = new Map(earlier.map(edit => [getEditKey(edit), edit]));
    later.forEach(edit => byKey.set(getEditKey(edit), edit));
    return [...byKey.values()];
};

type GroupedUpdate = { deleted: boolean; edits: RemoteEdit[] };

/** Groups a burst of updates by node, merging the edits of each one. */
export const groupUpdates = (updates: NodeUpdate[]) =>
    updates.reduce((grouped, update) => {
        const current = grouped.get(update.nodeId) ?? { deleted: false, edits: [] };
        grouped.set(
            update.nodeId,
            update.type === NodeUpdateType.Deleted
                ? { ...current, deleted: true }
                : { ...current, edits: mergeEdits(current.edits, update.edits) },
        );
        return grouped;
    }, new Map<string, GroupedUpdate>());

type EventValue = {
    languageIsoCode: string;
    value: unknown;
    code?: string;
    measure?: string;
    translate?: boolean;
    translating?: boolean;
    approvedValue?: string | null;
    autoGenerated?: boolean;
};

/** A link value can travel as the linked node itself: the grid holds its id. */
const toStoredValue = (value: unknown) =>
    value && typeof value === 'object' && 'id' in value ? (value as { id: string }).id : value;

/**
 * Turns an edit into the content the grid stores. The event carries one entry per value, the grid
 * one entry per language holding every value, the same grouping the read model applies.
 */
export const toNodeContent = (edit: RemoteEdit, repeatable = false): NodeContent => {
    const entries = (Array.isArray(edit.value) ? edit.value : []) as EventValue[];
    const value = entries.reduce<NodeContent['value']>((grouped, entry) => {
        const stored = toStoredValue(entry.value) as string;
        const group = grouped.find(g => g.languageIsoCode === entry.languageIsoCode);
        if (group) {
            return grouped.map(g => (g === group ? { ...g, value: [...g.value, stored] } : g));
        }
        return [
            ...grouped,
            {
                languageIsoCode: entry.languageIsoCode,
                value: [stored],
                ...(entry.code ? { code: entry.code } : {}),
                ...(entry.measure ? { measure: entry.measure } : {}),
                ...(entry.translate !== undefined ? { translate: entry.translate } : {}),
                ...(entry.translating !== undefined ? { translating: entry.translating } : {}),
                ...(entry.approvedValue != null ? { approvedValue: [entry.approvedValue] } : {}),
                ...(entry.autoGenerated !== undefined
                    ? { autoGenerated: entry.autoGenerated }
                    : {}),
            },
        ];
    }, []);

    return {
        contentTypeGroup: edit.contentTypeGroup,
        contentType: edit.contentType,
        contentTypeGroupIndex: edit.contentTypeGroupIndex,
        type: edit.type,
        repeatable,
        value,
    };
};

const toNodeContents = (handle: DataTableRemoteHandle, nodeId: string, edits: RemoteEdit[]) => {
    const node = handle.getNode(nodeId);
    return edits.map(edit =>
        toNodeContent(
            edit,
            node?.contents?.find(
                c =>
                    c.contentTypeGroup === edit.contentTypeGroup &&
                    c.contentType === edit.contentType,
            )?.repeatable ?? false,
        ),
    );
};

const mergeHighlight = (
    previous: CellHighlights[string] | undefined,
    next: CellHighlights[string],
): CellHighlights[string] =>
    next.row === 'deleted'
        ? next
        : {
              row: next.row ?? previous?.row,
              columns: { ...previous?.columns, ...next.columns },
          };

const mergeNoticeEntry = (
    previous: RemoteNoticeEntry | undefined,
    next: RemoteNoticeEntry,
): RemoteNoticeEntry =>
    previous
        ? {
              ...next,
              fieldCodes: [...new Set([...previous.fieldCodes, ...next.fieldCodes])],
              kind:
                  KIND_SEVERITY[previous.kind] > KIND_SEVERITY[next.kind]
                      ? previous.kind
                      : next.kind,
          }
        : next;

/** Keeps only the entries of the node ids the page still shows. */
const pickShown = <T>(record: Record<string, T>, shown: Set<string>) => {
    const kept = Object.entries(record).filter(([nodeId]) => shown.has(nodeId));
    return kept.length === Object.keys(record).length ? record : Object.fromEntries(kept);
};

type UseRemoteRowUpdatesArgs = {
    enabled: boolean;
    hierarchyIds: string[];
    /** Node ids of the current page. */
    shownIds: string[];
    remoteHandleRef: MutableRefObject<DataTableRemoteHandle | null>;
};

export function useRemoteRowUpdates({
    enabled,
    hierarchyIds,
    shownIds,
    remoteHandleRef,
}: UseRemoteRowUpdatesArgs) {
    const { sessionData } = useAuth();
    const username = sessionData?.username;

    const [highlights, setHighlights] = useState<CellHighlights>({});
    const [noticeByNode, setNoticeByNode] = useState<Record<string, RemoteNoticeEntry>>({});
    const [conflicts, setConflicts] = useState<Record<string, RemoteConflict>>({});

    const bufferRef = useRef<NodeUpdate[]>([]);
    const timerRef = useRef<ReturnType<typeof setTimeout>>();

    // Sorted so the same page in a different order does not reopen the subscription.
    const nodeIds = useMemo(() => [...shownIds].sort(), [shownIds]);

    const flush = useCallback(() => {
        const handle = remoteHandleRef.current;
        const updates = bufferRef.current;
        bufferRef.current = [];
        if (!handle || updates.length === 0) return;

        const nextHighlights: CellHighlights = {};
        const nextNotice: Record<string, RemoteNoticeEntry> = {};
        const nextConflicts: Record<string, RemoteConflict> = {};

        groupUpdates(updates).forEach(({ deleted, edits }, nodeId) => {
            const code = handle.getNode(nodeId)?.code ?? nodeId;
            const columns = [...new Set(edits.map(getEditColumnId))];

            if (deleted) {
                nextHighlights[nodeId] = { row: 'deleted' };
                nextNotice[nodeId] = { nodeId, code, fieldCodes: [], kind: 'deleted' };
                return;
            }
            if (edits.length === 0) return;

            const openColumns = handle
                .getEditingColumns(nodeId)
                .filter(column => columns.includes(column));

            if (openColumns.length) {
                nextConflicts[nodeId] = { nodeId, code, edits, openColumns };
                nextHighlights[nodeId] = {
                    row: 'conflict',
                    columns: markColumns(columns, 'conflict'),
                };
                nextNotice[nodeId] = { nodeId, code, fieldCodes: columns, kind: 'conflict' };
            } else {
                handle.applyRemoteContents(nodeId, toNodeContents(handle, nodeId, edits));
                nextHighlights[nodeId] = { columns: markColumns(columns, 'updated') };
                nextNotice[nodeId] = { nodeId, code, fieldCodes: columns, kind: 'updated' };
            }
        });

        setHighlights(previous =>
            Object.entries(nextHighlights).reduce(
                (merged, [nodeId, next]) => ({
                    ...merged,
                    [nodeId]: mergeHighlight(previous[nodeId], next),
                }),
                previous,
            ),
        );
        setNoticeByNode(previous =>
            Object.entries(nextNotice).reduce(
                (merged, [nodeId, next]) => ({
                    ...merged,
                    [nodeId]: mergeNoticeEntry(previous[nodeId], next),
                }),
                previous,
            ),
        );
        setConflicts(previous => {
            const merged = { ...previous };
            // A delete settles any conflict the node had: there is nothing left to keep.
            Object.entries(nextHighlights).forEach(([nodeId, highlight]) => {
                if (highlight.row === 'deleted') delete merged[nodeId];
            });
            Object.values(nextConflicts).forEach(conflict => {
                const existing = merged[conflict.nodeId];
                merged[conflict.nodeId] = existing
                    ? {
                          ...conflict,
                          edits: mergeEdits(existing.edits, conflict.edits),
                          openColumns: [
                              ...new Set([...existing.openColumns, ...conflict.openColumns]),
                          ],
                      }
                    : conflict;
            });
            return merged;
        });
    }, [remoteHandleRef]);

    useNodesUpdatedSubscription({
        shouldResubscribe: true,
        variables: { hierarchyIds, nodeIds, username },
        skip: !enabled || !username || nodeIds.length === 0 || hierarchyIds.length === 0,
        onData: ({ data }) => {
            const update = data.data?.nodesUpdated;
            if (!update) return;
            bufferRef.current.push(update);
            clearTimeout(timerRef.current);
            timerRef.current = setTimeout(flush, REMOTE_UPDATES_FLUSH_MS);
        },
    });

    useEffect(() => () => clearTimeout(timerRef.current), []);

    // A row leaving the page takes its highlight and its conflict with it.
    useEffect(() => {
        const shown = new Set(shownIds);
        setHighlights(previous => pickShown(previous, shown));
        setConflicts(previous => pickShown(previous, shown));
        setNoticeByNode(previous => pickShown(previous, shown));
    }, [shownIds]);

    const acceptConflict = useCallback(
        (nodeId: string) => {
            const conflict = conflicts[nodeId];
            const handle = remoteHandleRef.current;
            if (!conflict || !handle) return;

            handle.applyRemoteContents(nodeId, toNodeContents(handle, nodeId, conflict.edits));
            const columns = [...new Set(conflict.edits.map(getEditColumnId))];
            setHighlights(previous => ({
                ...previous,
                [nodeId]: {
                    columns: { ...previous[nodeId]?.columns, ...markColumns(columns, 'updated') },
                },
            }));
            setNoticeByNode(previous =>
                previous[nodeId]
                    ? { ...previous, [nodeId]: { ...previous[nodeId], kind: 'updated' } }
                    : previous,
            );
            setConflicts(({ [nodeId]: _accepted, ...rest }) => rest);
        },
        [conflicts, remoteHandleRef],
    );

    // The row keeps its conflict highlight until the next refresh; the user's own later save
    // overwrites the incoming change.
    const keepMine = useCallback((nodeId: string) => {
        setConflicts(({ [nodeId]: _dropped, ...rest }) => rest);
    }, []);

    const dismissNotice = useCallback(() => setNoticeByNode({}), []);

    const reset = useCallback(() => {
        bufferRef.current = [];
        clearTimeout(timerRef.current);
        setHighlights({});
        setNoticeByNode({});
        setConflicts({});
    }, []);

    const notice = useMemo(() => Object.values(noticeByNode), [noticeByNode]);

    return {
        highlights,
        notice,
        conflicts,
        acceptConflict,
        keepMine,
        dismissNotice,
        reset,
    };
}
